// Shared Wheel-visibility picker — ONE component for every surface that lets an
// owner choose Public / Private / Hidden.
//
// Replaces two divergent inline copies (the Create-Wheel wizard's WheelVisibility
// step and the Segment Builder's Privacy tab). Options, copy and icons all come from
// src/constants/wheelVisibility.js so the surfaces cannot drift apart again.
//
// What changed for the user:
//   * all three options are always shown (never a two-option subset);
//   * each option's explanation is now INLINE and terse — "Visible to everyone",
//     "Visible only by shared link", "Visible only to me" — instead of being hidden
//     behind a hover-only tooltip that a touch user can never open;
//   * each option carries the SAME icon the Wheel will wear on its card once it
//     exists (ASSETS.segmentEditor), so the picker teaches the card's vocabulary.
//
// Accessibility:
//   * the options are distinguishable by icon SHAPE (globe / crossed-eye /
//     incognito mask) and by their label and description, so nothing depends on
//     color alone (WCAG 1.4.1). The icons DO carry the app's existing per-state
//     colors (green / amber / purple) because that mapping is already shipped on
//     every Wheel card — but it is reinforcement, never the only signal.
//   * each radio's accessible name is "<label>. <description>", so a screen reader
//     announces the consequence, not just the word.
//   * rows are >= 44px tall, clearing the touch-target ruling.
//   * the hover/selection transition is dropped under prefers-reduced-motion.
//
// The onChange contract is deliberately the native one — handlers receive a real
// change event and read `event.target.value` (a string), exactly as the previous MUI
// RadioGroup call sites did, so no caller logic had to change.
import React from "react";
import PropTypes from "prop-types";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { styled } from "@mui/material/styles";
import TextView from "../TextView";
import { WHEEL_VISIBILITY_OPTIONS } from "../../constants/wheelVisibility";
import { colors } from "../../utils";
import { ASSETS } from "../../utils/config";

const INK = "#123051"; // brand navy — the app's default ink (13.4:1 on white)
// Secondary ink for the descriptions. Measured, not guessed: this is 5.3:1 on the
// dialog's white paper, clearing WCAG AA (4.5:1) for 13px body text. An earlier,
// lighter #6B7C8F measured 4.28:1 and failed.
const MUTED = "#5A6B7D";

const OptionRadio = styled(Radio)({
  alignSelf: "flex-start",
  padding: "8px",
  // The row is top-aligned (alignItems: flex-start) so the radio, the icon and the
  // option title share the first line. The radio's own 8px top padding, however,
  // drops its 20px glyph ~7px below the icon/title line, so the icons read as
  // floating above the radios. Pull the glyph back up onto that line without
  // touching the padding (the padding stays symmetric for the ripple/touch circle).
  marginTop: "-7px",
  color: colors.PrimaryPurple300,
  "&.Mui-checked": { color: colors.PrimaryPurple500 },
  // The native input is visually hidden, so without this a keyboard user gets no
  // focus indicator at all. `Mui-focusVisible` fires only for keyboard focus, so
  // clicking with a mouse does not leave a ring behind.
  "&.Mui-focusVisible": {
    outline: `2px solid ${colors.PrimaryPurple500}`,
    outlineOffset: "2px",
    borderRadius: "50%",
  },
});

// The whole row is the click surface (the radio, the icon, the label and the
// description), so the target is the full width rather than a 20px dot.
// `shouldForwardProp` is required: this is MUI's emotion-backed `styled`, which has
// no transient-prop convention, so without it `selected` would be written onto the
// DOM node and React would warn about an unknown attribute.
const OptionRow = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== "$selected",
})(({ $selected }) => ({
  alignItems: "flex-start",
  margin: 0,
  padding: "6px 10px 6px 2px",
  minHeight: 44,
  borderRadius: 10,
  width: "100%",
  transition: "background-color 120ms ease",
  "&:hover": { backgroundColor: "rgba(143, 56, 239, 0.06)" },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  "& .MuiFormControlLabel-label": {
    width: "100%",
    color: $selected ? colors.PrimaryPurple500 : INK,
  },
}));

function WheelVisibilityOptions({
  value,
  onChange,
  label,
  name = "wheel-visibility",
  options = WHEEL_VISIBILITY_OPTIONS,
  style,
}) {
  const groupLabel = label || "Wheel visibility";

  return (
    <FormControl component="fieldset" style={{ width: "100%", ...style }}>
      {label ? (
        <TextView variant="subtitle" style={{ marginBottom: 8 }}>
          {label}
        </TextView>
      ) : null}
      <RadioGroup
        aria-label={groupLabel}
        name={name}
        value={value ?? ""}
        onChange={onChange}
      >
        {options.map((option) => {
          const selected = String(option.value) === String(value);
          return (
            <OptionRow
              key={option.key}
              $selected={selected}
              value={option.value}
              control={
                <OptionRadio
                  inputProps={{
                    "aria-label": `${option.label}. ${option.description}`,
                  }}
                />
              }
              label={
                <span
                  style={{ display: "flex", alignItems: "flex-start", gap: 10 }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      marginTop: 1,
                      flexShrink: 0,
                      // The canonical marks carry their own baked colors, so this
                      // only dims the ones that are not chosen — the selected row
                      // shows its icon at full strength.
                      opacity: selected ? 1 : 0.75,
                    }}
                  >
                    <img
                      src={ASSETS.segmentEditor[option.icon]}
                      width={20}
                      height={20}
                      alt=""
                      aria-hidden="true"
                      data-testid={`visibility-icon-${option.icon}`}
                    />
                  </span>
                  <span style={{ display: "block", minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        fontFamily: "DM Sans, sans-serif",
                        fontSize: 14,
                        fontWeight: selected ? 600 : 500,
                        lineHeight: "20px",
                      }}
                    >
                      {option.label}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontFamily: "DM Sans, sans-serif",
                        fontSize: 13,
                        lineHeight: "18px",
                        color: MUTED,
                      }}
                    >
                      {option.description}
                    </span>
                  </span>
                </span>
              }
            />
          );
        })}
      </RadioGroup>
    </FormControl>
  );
}

WheelVisibilityOptions.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  name: PropTypes.string,
  options: PropTypes.array,
  style: PropTypes.object,
};

export default WheelVisibilityOptions;
